import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';
import { FaGem } from 'react-icons/fa';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const themes = {
  light: {
    backgroundColor: 'bg-white',
    textColor: 'text-gray-800',
    borderColor: 'border-gray-300',
    buttonColor: 'bg-purple-600',
    buttonHoverColor: 'hover:bg-purple-700',
    shadow: 'shadow-md'
  },
  dark: {
    backgroundColor: 'bg-gray-800',
    textColor: 'text-white',
    borderColor: 'border-gray-600',
    buttonColor: 'bg-purple-800',
    buttonHoverColor: 'hover:bg-purple-900',
    shadow: 'shadow-none'
  },
  gem: {
    backgroundColor: 'bg-teal-50',
    textColor: 'text-gray-800',
    borderColor: 'border-teal-300',
    buttonColor: 'bg-teal-600',
    buttonHoverColor: 'hover:bg-teal-700',
    shadow: 'shadow-md'
  }
};

const DiamondCaratCalculator = () => {
  const [inputWeight, setInputWeight] = useState('');
  const [inputUnit, setInputUnit] = useState('grams');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [theme, setTheme] = useState('light');
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    // Load the saved theme from localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  const handleInputChange = (e) => {
    setInputWeight(e.target.value);
    setError('');
  };

  const handleUnitChange = (e) => {
    setInputUnit(e.target.value);
  };

  const calculateCaratWeight = () => {
    if (!inputWeight) {
      setError('Please enter a weight');
      return;
    }

    try {
      const weight = parseFloat(inputWeight);

      if (isNaN(weight) || weight <= 0) {
        setError('Please enter a valid weight greater than zero');
        return;
      }

      let caratWeight;
      switch (inputUnit) {
        case 'grams':
          caratWeight = weight / 0.2;
          break;
        case 'milligrams':
          caratWeight = weight / 200;
          break;
        case 'ounces':
          caratWeight = weight * 141.7476;
          break;
        default:
          setError('Invalid unit selected');
          return;
      }

      setResult({
        inputWeight: weight,
        inputUnit,
        caratWeight: caratWeight.toFixed(2)
      });

      // Generate chart data
      const weights = [0.1, 0.5, 1, 2, 5, 10];
      const caratWeights = weights.map(w => {
        switch (inputUnit) {
          case 'grams':
            return (w / 0.2).toFixed(2);
          case 'milligrams':
            return (w / 200).toFixed(2);
          case 'ounces':
            return (w * 141.7476).toFixed(2);
          default:
            return 0;
        }
      });

      setChartData({
        labels: weights,
        datasets: [
          {
            label: 'Carat Weight',
            data: caratWeights,
            borderColor: 'rgba(75,192,192,1)',
            tension: 0.1,
            fill: false
          }
        ]
      });
    } catch (error) {
      setError('An error occurred while calculating the carat weight');
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : theme === 'dark' ? 'gem' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme); // Save the new theme to localStorage
  };

  const currentTheme = themes[theme];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className={`min-h-screen ${currentTheme.backgroundColor} ${currentTheme.textColor}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Diamond Carat Calculator</title>
          <meta name="description" content="Calculate the carat weight of diamonds from different units." />
          <meta name="keywords" content="diamond carat calculator, gemstone weight, carat conversion" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8 max-w-2xl mx-auto">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-purple-600 mr-4">
              <FaGem />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">Diamond Carat Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The Diamond Carat Calculator helps you convert the weight of diamonds from different units to carats.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Weight:</label>
              <input
                type="number"
                value={inputWeight}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border ${currentTheme.borderColor} rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                placeholder="Enter weight"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Unit:</label>
              <select
                value={inputUnit}
                onChange={handleUnitChange}
                className={`w-full px-4 py-3 border ${currentTheme.borderColor} rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
              >
                <option value="grams">Grams</option>
                <option value="milligrams">Milligrams</option>
                <option value="ounces">Ounces</option>
              </select>
            </div>
          </div>

          <button
            onClick={calculateCaratWeight}
            className={`w-full py-3 px-4 ${currentTheme.buttonColor} text-white font-medium rounded-lg ${currentTheme.buttonHoverColor} focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors duration-200`}
          >
            Calculate Carat Weight
          </button>

          <button
            onClick={toggleTheme}
            className="mt-4 py-2 px-4 bg-gray-200 text-gray-800 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
          >
            {theme === 'light' ? 'Dark Theme' : theme === 'dark' ? 'Gem Theme' : 'Light Theme'}
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Carat Weight Results</h2>
              
              <div className={`bg-gray-50 p-6 rounded-lg ${currentTheme.shadow}`}>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Input Weight</h3>
                <p className="text-xl font-bold">{result.inputWeight} {result.inputUnit}</p>
              </div>

              <div className={`bg-gray-50 p-6 rounded-lg mt-6 ${currentTheme.shadow}`}>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Carat Weight</h3>
                <p className="text-xl font-bold">{result.caratWeight} carats</p>
              </div>

              {chartData && (
                <div className={`bg-white p-6 rounded-lg shadow-md mt-8 ${currentTheme.shadow}`}>
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Carat Weight'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Weight in selected unit'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Weight to Carat Conversion'
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Diamond Carat Calculator <span className="text-purple-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Diamond Carat Calculator effectively:</p>
            
            <div className="step-section mt-4">
              <h3 className="text-lg font-semibold text-purple-600 mb-2">Step 1: Enter Weight</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Input the weight of the diamond in the desired unit (grams, milligrams, or ounces).</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-purple-600 mb-2">Step 2: Select Unit</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Select the unit of the input weight from the dropdown menu.</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-purple-600 mb-2">Step 3: Calculate</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Click the "Calculate Carat Weight" button to get the results.</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-purple-600 mb-2">Step 4: Review Results</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Check the calculated carat weight.</li>
                <li>Visualize the conversion with the graph.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ <span className="text-purple-600">❓</span></h2>
          <div className="text-gray-600">
            <div className="faq-item mb-4">
              <h3 className="text-lg font-semibold text-purple-600 mb-2">What is a carat?</h3>
              <p>A carat is a unit of weight used for gemstones, where 1 carat equals 0.2 grams.</p>
            </div>

            <div className="faq-item mb-4">
              <h3 className="text-lg font-semibold text-purple-600 mb-2">Why is carat weight important?</h3>
              <p>Carat weight is important because it is a standard unit used in the gemstone industry to measure the weight of gemstones. It helps in pricing and comparison.</p>
            </div>

            <div className="faq-item mb-4">
              <h3 className="text-lg font-semibold text-purple-600 mb-2">How do I convert grams to carats?</h3>
              <p>To convert grams to carats, divide the weight in grams by 0.2.</p>
            </div>
          </div>
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-purple-600">📈</span></h2>
          <div className="text-gray-600">
            <p>Let's say you have a diamond that weighs 0.4 grams.</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Enter 0.4 in the weight field.</li>
              <li>Select "grams" as the unit.</li>
              <li>Click the "Calculate Carat Weight" button.</li>
              <li>The calculator will show that the carat weight is 2.00 carats (0.4 grams / 0.2 = 2.00 carats).</li>
              <li>The graph will visualize this conversion.</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DiamondCaratCalculator;